import { useCallback, useEffect, useRef } from 'react';
import i18n from 'i18next';
import { useLoginInfo } from '~/shell/hooks/useLoginInfo';
import { formatCustomerNumberNoSlash } from '~/common/utils/sessionCustomerUtil';
import type {
  IAvailabilityOptionDtoData,
  ICartPositionAvailabilityOptionsDtoData,
  IGroupedOrderPositionDTOData,
  IOrderCurrentDTOData,
  IWebshopLoginInfoDtoData
} from '~/dtos/ProdegaApiWebshop-api-typings-generated';
import type { BaseArticleDetail } from '~/common/types/baseArticleDetail';
import { useLocation, useMatches } from 'react-router';
import { useCart } from '../features/cart/state-hooks/useCart.ts';
import type { AddArticleToTemplateInput } from '~/features/templates/routes/resources/AddArticleToTemplateRoute';
import type { RouteTrackingData } from '~/tracking/trackingTypes.ts';
import type { ContextLoginInfo } from '../shell/globalContext';
import { isTrackingLogEnabled } from './logger';
import { getLogger, type Logger } from '~/logging/logger.ts';

let eventsToSendLater: { trackingType: TrackingType; trackingInfo: TrackingInfo }[] = [];

type GoogleAnalyticsEvent =
  | 'pageview'
  | 'detail'
  | 'search'
  | 'add'
  | 'add_to_list'
  | 'remove'
  | 'cart'
  | 'purchase'
  | 'overlay'
  | 'availabilityOverlay_option'
  | 'navigation';
type TrackingType = 'view' | 'link';
type TrackingInfo = ReturnType<typeof createDefaultTrackingInfo>;
type ProductDetailTrackingInfo = ReturnType<typeof createProductDetailTrackingInfo>;
type SearchTrackingInfo = ReturnType<typeof createSearchTrackingInfo>;
type PurchaseTrackingInfo = ReturnType<typeof createPurchaseTrackingInfo>;
type AddPositionTrackingInfo = ReturnType<typeof createAddCartPositionTrackingInfo>;
type AddToListTrackingInfo = ReturnType<typeof createAddToListTrackingInfo>;
type RemovePositionsTrackingInfo = ReturnType<typeof createRemoveCartPositionsTrackingInfo>;
type PositionAvailabilityOptionsInfo = ReturnType<typeof createOverlayPositionAvailabilityOptionInfo>;
type PositionAvailabilitySelectionInfo = ReturnType<typeof createPositionAvailabilitySelectionInfo>;
type NavigationTrackingInfo = ReturnType<typeof createNavigationTrackingInfo>;
type GoogleAnalyticsEventTrackingData = Partial<
  | ProductDetailTrackingInfo
  | SearchTrackingInfo
  | PurchaseTrackingInfo
  | AddPositionTrackingInfo
  | AddToListTrackingInfo
  | RemovePositionsTrackingInfo
  | PositionAvailabilityOptionsInfo
  | PositionAvailabilitySelectionInfo
  | NavigationTrackingInfo
>;

export type GoogleAnalyticsCartPosition = {
  articleNumber: string;
  quantity: number;
  articleDescription: string;
};

export const GOOGLE_ANALYTICS_NAVIGATION_ACTION = {
  MainNavigation: 'main navigation'
} as const;

export const GOOGLE_ANALYTICS_NAVIGATION_LABEL = {
  FavoriteTemplate: 'FAVORITE_TEMPLATE'
} as const;

export function useSendNavigationToGoogleAnalyticsEffect() {
  const location = useLocation();
  const { loginInfo } = useLoginInfo();
  const sendViewToGoogleAnalytics = useSendViewToGoogleAnalytics();

  useEffect(() => {
    sendViewToGoogleAnalytics('pageview', loginInfo);
  }, [location, loginInfo]); // eslint-disable-line react-hooks/exhaustive-deps
}

export function useSendDetailViewToGoogleAnalyticsEffect(articleDetail: BaseArticleDetail) {
  const { loginInfo } = useLoginInfo();
  const sendViewToGoogleAnalytics = useSendViewToGoogleAnalytics();

  useEffect(() => {
    const detailTrackingData = createProductDetailTrackingInfo(articleDetail);
    sendViewToGoogleAnalytics('detail', loginInfo, detailTrackingData);
  }, [articleDetail, loginInfo]); // eslint-disable-line react-hooks/exhaustive-deps
}

export function useSendCartViewToGoogleAnalyticsEffect() {
  const { loginInfo } = useLoginInfo();
  const { positions } = useCart();
  const sendViewToGoogleAnalytics = useSendViewToGoogleAnalytics();

  useEffect(() => {
    if (positions && positions.length > 0) {
      const cartTrackingData = createCartTrackingInfo(positions);
      sendViewToGoogleAnalytics('cart', loginInfo, cartTrackingData);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
}

export function useSendSearchToGoogleAnalyticsEffect(searchTerm: string | undefined, totalCount: number | undefined) {
  const { loginInfo } = useLoginInfo();
  const sendViewToGoogleAnalytics = useSendViewToGoogleAnalytics();
  const normalizedSearchTerm = searchTerm?.trim();
  // Keep this outside React state to dedupe repeated effect runs without triggering another render.
  const lastSentSearchTerm = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (normalizedSearchTerm && totalCount !== undefined && lastSentSearchTerm.current !== normalizedSearchTerm) {
      lastSentSearchTerm.current = normalizedSearchTerm;
      const searchTrackingData = createSearchTrackingInfo(normalizedSearchTerm, totalCount);
      sendViewToGoogleAnalytics('search', loginInfo, searchTrackingData);
    }
  }, [normalizedSearchTerm, totalCount]); // eslint-disable-line react-hooks/exhaustive-deps
}

export function useSendPositionAvailabilityOptionsToGoogleAnalyticsEffect(
  positionAvailabilityOptions: ICartPositionAvailabilityOptionsDtoData[] | undefined
) {
  const { loginInfo } = useLoginInfo();
  const sendLinkToGoogleAnalytics = useSendLinkToGoogleAnalytics();
  useEffect(() => {
    if (positionAvailabilityOptions && positionAvailabilityOptions.length > 0) {
      for (const positionAvailabilityOption of positionAvailabilityOptions) {
        const overlayPositionAvailabilityOptionsData =
          createOverlayPositionAvailabilityOptionInfo(positionAvailabilityOption);
        sendLinkToGoogleAnalytics('overlay', loginInfo, overlayPositionAvailabilityOptionsData);
      }
    }
  }, [positionAvailabilityOptions]); // eslint-disable-line react-hooks/exhaustive-deps
}

export function useSendPositionAvailabilitySelectionsToGoogleAnalytics() {
  const { loginInfo } = useLoginInfo();
  const sendLinkToGoogleAnalytics = useSendLinkToGoogleAnalytics();

  function sendAvailabilitySelectionToGoogleAnalytics(selectedAvailabilityOptions: IAvailabilityOptionDtoData[]) {
    for (const selectedAvailabilityOption of selectedAvailabilityOptions) {
      const availabilitySelectionInfo = createPositionAvailabilitySelectionInfo(selectedAvailabilityOption);
      sendLinkToGoogleAnalytics('availabilityOverlay_option', loginInfo, availabilitySelectionInfo);
    }
  }

  return sendAvailabilitySelectionToGoogleAnalytics;
}

export function useSendPurchaseToGoogleAnalytics() {
  const sendLinkToGoogleAnalytics = useSendLinkToGoogleAnalytics();

  function sendTrackingData(order: IOrderCurrentDTOData, loginInfo: IWebshopLoginInfoDtoData | undefined) {
    const purchaseTrackingData = createPurchaseTrackingInfo(order);
    sendLinkToGoogleAnalytics('purchase', loginInfo, purchaseTrackingData);
  }

  return sendTrackingData;
}

export function useSendAddCartPositionToGoogleAnalytics() {
  const sendLinkToGoogleAnalytics = useSendLinkToGoogleAnalytics();

  function sendTrackingData(
    articleNumber: string,
    quantity: number,
    articleDescription: string,
    loginInfo: IWebshopLoginInfoDtoData | undefined
  ) {
    const addCartPositionTrackingData = createAddCartPositionTrackingInfo(articleNumber, quantity, articleDescription);
    sendLinkToGoogleAnalytics('add', loginInfo, addCartPositionTrackingData);
  }

  return sendTrackingData;
}

export function useSendRemoveCartPositionsToGoogleAnalytics() {
  const sendLinkToGoogleAnalytics = useSendLinkToGoogleAnalytics();

  function sendTrackingData(positions: GoogleAnalyticsCartPosition[], loginInfo: IWebshopLoginInfoDtoData | undefined) {
    const removeCartPositionsTrackingData = createRemoveCartPositionsTrackingInfo(positions);
    if (removeCartPositionsTrackingData) {
      sendLinkToGoogleAnalytics('remove', loginInfo, removeCartPositionsTrackingData);
    }
  }

  return sendTrackingData;
}

export function useSendAddToListToGoogleAnalytics() {
  const { loginInfo } = useLoginInfo();
  const sendLinkToGoogleAnalytics = useSendLinkToGoogleAnalytics();

  function sendTrackingData(articles: AddArticleToTemplateInput[]) {
    const positions = articles
      .filter(article => article.articleNumber !== undefined && article.articleDescriptionText !== undefined)
      .map(article => ({
        articleNumber: article.articleNumber as string,
        quantity: article.quantity,
        articleDescription: article.articleDescriptionText as string
      }));
    const addToListTrackingData = createAddToListTrackingInfo(positions);
    if (addToListTrackingData) {
      sendLinkToGoogleAnalytics('add_to_list', loginInfo, addToListTrackingData);
    }
  }

  return sendTrackingData;
}

export function useSendNavigationClickToGoogleAnalytics() {
  const { loginInfo } = useLoginInfo();
  const sendLinkToGoogleAnalytics = useSendLinkToGoogleAnalytics();

  function sendTrackingData(
    eventLabel: string,
    eventAction: string = GOOGLE_ANALYTICS_NAVIGATION_ACTION.MainNavigation
  ) {
    const navigationTrackingData = createNavigationTrackingInfo(eventAction, eventLabel);
    sendLinkToGoogleAnalytics('navigation', loginInfo, navigationTrackingData);
  }

  return sendTrackingData;
}

function useSendViewToGoogleAnalytics() {
  const sendDataToGoogleAnalytics = useSendDataToGoogleAnalytics();
  function sendTrackingData(
    eventName: GoogleAnalyticsEvent,
    loginInfo: ContextLoginInfo | undefined,
    eventTrackingData?: GoogleAnalyticsEventTrackingData
  ) {
    sendDataToGoogleAnalytics('view', eventName, loginInfo, eventTrackingData);
  }

  return sendTrackingData;
}

function useSendLinkToGoogleAnalytics() {
  const sendDataToGoogleAnalytics = useSendDataToGoogleAnalytics();
  function sendTrackingData(
    eventName: GoogleAnalyticsEvent,
    loginInfo: ContextLoginInfo | undefined,
    eventTrackingData?: GoogleAnalyticsEventTrackingData
  ) {
    sendDataToGoogleAnalytics('link', eventName, loginInfo, eventTrackingData);
  }

  return sendTrackingData;
}

const log = getLogger('google-analytics');

function useSendDataToGoogleAnalytics() {
  const routeMatches = useMatches();
  const currentRouteMatch = routeMatches.at(-1);
  const handle = currentRouteMatch?.handle as any;

  const sendTrackingData = useCallback(
    (
      trackingType: TrackingType,
      eventName: GoogleAnalyticsEvent,
      loginInfo: ContextLoginInfo | undefined,
      eventTrackingData?: GoogleAnalyticsEventTrackingData
    ) => {
      const trackingConfig = window.TG_CLIENT_CONFIG?.trackingConfig;
      if (!trackingConfig) return;

      // Bots werden für beide Sinks (Hub-Elastic-Tracing + Tealium/GA) ausgeschlossen,
      // analog zum bestehenden TealiumScript-Verhalten.
      if (trackingConfig.isBot) return;

      const routeTrackingData = handle?.getRouteTrackingData?.(currentRouteMatch?.loaderData) as RouteTrackingData;
      const enableTrackingLog = isTrackingLogEnabled();

      const defaultTrackingInfo = createDefaultTrackingInfo(eventName, loginInfo);
      const googleAnalyticsRouteTrackingData = {
        page_pageInfo_pageName: routeTrackingData?.pageName || defaultTrackingInfo.page_pageInfo_pageName
      };
      const trackingInfo = { ...defaultTrackingInfo, ...googleAnalyticsRouteTrackingData, ...eventTrackingData };

      if (trackingConfig.enableHubTracing) {
        sendToHubElasticTracing(trackingType, eventName, trackingInfo, enableTrackingLog);
      }

      if (trackingConfig.enableTealium) {
        sendToTealium(trackingType, eventName, trackingInfo, enableTrackingLog);
      }
    },
    [currentRouteMatch, handle]
  );

  return sendTrackingData;
}

// Sink 1: Hub-Elastic-Tracing via eigener Frontend-Route /infra/tracking/log
// (server-seitig wird daraus ein POST an die Hub Search API gemacht; siehe tracing.server.ts)
function sendToHubElasticTracing(
  trackingType: TrackingType,
  eventName: GoogleAnalyticsEvent,
  trackingInfo: TrackingInfo,
  enableTrackingLog: boolean
) {
  if (enableTrackingLog) {
    log.debug(`Sending ${trackingType} '${eventName}' to Hub-Elastic-Tracing`);
  }
  fetch('/infra/tracking/log', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ eventName, trackingType, trackingInfo })
  })
    .then(response => {
      if (!response.ok && enableTrackingLog) {
        log.warn(`Hub-Elastic-Tracing returned non-ok status ${response.status} for event '${eventName}'`);
      }
    })
    .catch(() => {
      // silent fail — Tracking darf die User-Experience nicht stören
    });
}

// Sink 2: Tealium / Google Analytics
function sendToTealium(
  trackingType: TrackingType,
  eventName: GoogleAnalyticsEvent,
  trackingInfo: TrackingInfo,
  enableTrackingLog: boolean
) {
  const utag = (window as any).utag;

  if (utag) {
    sendQueuedEvents(enableTrackingLog, log);

    if (enableTrackingLog) {
      log.debug(
        formatGoogleAnalyticsLogMessage(`Sending ${trackingType} '${eventName}' to Google Analytics`, trackingType, {
          ...trackingInfo
        })
      ); // logging a cloned object, because the object is mutated by `utag.view`
    }
    if (trackingType === 'view') {
      utag.view(trackingInfo);
    } else if (trackingType === 'link') {
      utag.link(trackingInfo);
    }
  } else {
    eventsToSendLater = [...eventsToSendLater.slice(-9), { trackingType, trackingInfo }]; // nur die letzten 10 Events speichern
    setTimeout(() => sendQueuedEvents(enableTrackingLog, log), 3000);
    if (enableTrackingLog) {
      log.debug(
        formatGoogleAnalyticsLogMessage(
          `Not sending ${trackingType} '${eventName}' to Google Analytics, because 'window.utag' is not available`,
          trackingType,
          {
            ...trackingInfo
          }
        )
      );
    }
  }
}

function createDefaultTrackingInfo(eventName: GoogleAnalyticsEvent, loginInfo: ContextLoginInfo | undefined) {
  const language = i18n.language;

  const defaultTrackingInfo = {
    platform: 'tgch-transgourmet-webshop-neu',
    event_attributes_eventName: eventName,
    page_category_00: language,
    page_category_01: 'shop',
    page_pageInfo_language: language,
    page_pageInfo_pageName: 'Transgourmet Web Shop',
    page_attributes_URL: window.location.href,
    user_profile_profileInfo_language: language,
    user_profile_profileInfo_loginStatus: loginInfo ? 'registered' : 'anonymous',
    user_profile_profileInfo_profileID: loginInfo ? formatCustomerNumberNoSlash(loginInfo) : ''
  };
  return defaultTrackingInfo;
}

function createProductDetailTrackingInfo(articleDetail: BaseArticleDetail) {
  const cartTrackingData = {
    product_productInfo_productName: [articleDetail.description ?? ''],
    product_productInfo_sku: [articleDetail.articleNumber],
    product_attributes_quantity: [1]
  };
  return cartTrackingData;
}

function createSearchTrackingInfo(searchTerm: string, totalCount: number) {
  const searchTrackingData = {
    onsiteSearch_category: 'global search',
    onsiteSearch_Type: 'standard',
    onsiteSearch_searchTerm: searchTerm,
    onsiteSearch_searchResults: `${totalCount}`
  };
  return searchTrackingData;
}

function createCartTrackingInfo(positions: IGroupedOrderPositionDTOData[]) {
  const cartTrackingData = {
    product_productInfo_productName: positions.flatMap(gpos => gpos.positions.map(pos => pos.articleDescriptionText)),
    product_productInfo_sku: positions.flatMap(gpos => gpos.positions.map(pos => pos.articleBaseNo)),
    product_attributes_quantity: positions.flatMap(gpos => gpos.positions.map(pos => pos.orderQuantity))
  };
  return cartTrackingData;
}

function createPurchaseTrackingInfo(order: IOrderCurrentDTOData) {
  const cartTrackingData = createCartTrackingInfo(order.normalPositions.concat(order.profitPlusPositions));
  const purchaseTrackingData = {
    ...cartTrackingData,
    transaction_transactionID: `${order.id}`,
    transaction_total_totalPrice: `${order.total}`,
    // basePrice wäre gemäss GA der Preis ohne Lieferkosten, da wir keine expliziten Lieferkosten ausweisen, ist
    // meine Annahme, dass als basePrice derselbe Wert wie für totalPrice geliefert werden muss.
    transaction_total_basePrice: `${order.total}`
  };
  return purchaseTrackingData;
}

function createAddCartPositionTrackingInfo(articleNumber: string, quantity: number, articleDescription: string) {
  const addPositionTrackingData = {
    event_category: 'conversions & leads',
    event_action: 'ecommerce',
    event_label: 'add',
    product_productInfo_sku: [articleNumber],
    product_attributes_quantity: [quantity],
    product_productInfo_productName: [articleDescription]
  };
  return addPositionTrackingData;
}

function createRemoveCartPositionsTrackingInfo(positions: GoogleAnalyticsCartPosition[]) {
  if (positions.length === 0) return undefined;

  const removePositionsTrackingData = {
    event_category: 'conversions & leads',
    event_action: 'ecommerce',
    event_label: 'remove',
    product_productInfo_sku: positions.map(position => position.articleNumber),
    product_attributes_quantity: positions.map(position => position.quantity),
    product_productInfo_productName: positions.map(position => position.articleDescription)
  };
  return removePositionsTrackingData;
}

function createAddToListTrackingInfo(positions: GoogleAnalyticsCartPosition[]) {
  if (positions.length === 0) return undefined;

  const addToListTrackingData = {
    event_category: 'conversions & leads',
    event_action: 'ecommerce',
    event_label: 'add to list',
    product_productInfo_sku: positions.map(position => position.articleNumber),
    product_attributes_quantity: positions.map(position => position.quantity),
    product_productInfo_productName: positions.map(position => position.articleDescription)
  };
  return addToListTrackingData;
}

export function createNavigationTrackingInfo(eventAction: string, eventLabel: string) {
  const navigationTrackingData = {
    event_category: 'navigation',
    event_action: eventAction,
    event_label: eventLabel
  };
  return navigationTrackingData;
}

function createOverlayPositionAvailabilityOptionInfo(
  positionAvailabilityOption: ICartPositionAvailabilityOptionsDtoData
) {
  const overlayPositionAvailabilityOptionInfo = {
    event_category: 'Overlay',
    event_action: 'PositionAvailabilityOptions',
    event_label: positionAvailabilityOption.message
  };
  return overlayPositionAvailabilityOptionInfo;
}
function createPositionAvailabilitySelectionInfo(availabilitySelection: IAvailabilityOptionDtoData) {
  const overlayPositionAvailabilitySelectionInfo = {
    event_category: 'Leads',
    event_action: 'ecommerce',
    event_label: availabilitySelection.description
  };
  return overlayPositionAvailabilitySelectionInfo;
}

function formatGoogleAnalyticsLogMessage(message: string, trackingType: TrackingType, trackingInfo: TrackingInfo) {
  const utagFunction = trackingType === 'view' ? 'utag.view' : 'utag.link';
  return `${message}:\n${utagFunction}(${JSON.stringify(trackingInfo, null, 2)})`;
}

function sendQueuedEvents(enableTrackingLog: boolean, log: Logger) {
  try {
    if (eventsToSendLater.length > 0) {
      const utag = (window as any).utag;

      if (utag) {
        eventsToSendLater.forEach(event => {
          if (enableTrackingLog) {
            log.debug(
              formatGoogleAnalyticsLogMessage(
                `Sending queued ${event.trackingType} to Google Analytics, because 'window.utag' is now available`,
                event.trackingType,
                {
                  ...event.trackingInfo
                }
              )
            ); // logging a cloned object, because the object is mutated by `utag.view`
          }
          if (event.trackingType === 'view') {
            utag.view(event.trackingInfo);
          } else if (event.trackingType === 'link') {
            utag.link(event.trackingInfo);
          }
        });
      } else if (enableTrackingLog) {
        log.debug(
          `Not sending ${eventsToSendLater.length} queued events to Google Analytics, because 'window.utag' is still not available!`
        );
      }
    }
  } finally {
    eventsToSendLater = [];
  }
}
